import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { SchemaAIHelper } from '@client/components/dynamic-form/SchemaAIHelper';
import {
    SchemaEditorTab,
    SchemaGeneralTab,
    SchemaPreviewTab,
    useSchemaForm,
} from '@client/components/dynamic-form/SchemaEditor';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import type { DynamicFormSchema } from '@shared/schemas/dynamic-forms/dynamic-form-schema';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Tabs } from 'antd';
import { FileCode } from 'lucide-react';
import { useCallback, useId, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/attestations/user-attestations/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Create form',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <FormSchemaCreate />
        </PageController>
    );
}

function FormSchemaCreate() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { mutate: createMutate } = trpc.formSchema.createFormSchema.useMutation();
    const { mutate: publishMutate } = trpc.formSchema.publishFormSchema.useMutation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isPublishing, setIsPublishing] = useState(false);

    // Track whether form has been generated (controls landing vs tabs view)
    const [formGenerated, setFormGenerated] = useState(false);
    const [activeTab, setActiveTab] = useState('preview');
    const [generatedSchemaKey, setGeneratedSchemaKey] = useState('new-form');

    const {
        schemaJson,
        parsedSchema,
        parseError,
        copied,
        formStore,
        formatJson,
        copySchema,
        updateSchema,
        updateSchemaJson,
    } = useSchemaForm({
        // No initial schema - start empty until form is generated
        initialSchema: undefined,
    });

    // Auto-generate schema key from form title and sync with JSON
    const handleTitleChange = useCallback((title: string) => {
        const generatedKey = title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
        setGeneratedSchemaKey(generatedKey || 'new-schema');
    }, []);

    const handleSchemaGenerated = useCallback(
        (schema: DynamicFormSchema) => {
            // Update the JSON editor with generated schema
            updateSchema(schema);

            if (schema.title) {
                form.setFieldValue('formTitle', schema.title);
                handleTitleChange(schema.title);
            }

            // Mark form as generated to switch to tabs view
            setFormGenerated(true);
            setActiveTab('preview');
        },
        [form, handleTitleChange, updateSchema],
    );

    const handleFormSubmit = useCallback(async (_response: unknown) => {
        message.success('Form submitted successfully!');
    }, []);

    const handleReset = useCallback(() => {
        if (formStore) {
            formStore.getState().reset();
        }
    }, [formStore]);

    const handleSaveAndPublish = useCallback(
        async (onClose: () => void) => {
            const toastKey = 'form-publish';
            try {
                const values = await form.validateFields();
                if (!parsedSchema) {
                    message.error('Please fix form errors before saving');
                    return;
                }

                setIsPublishing(true);
                message.loading({ content: 'Creating and publishing...', key: toastKey });

                createMutate(
                    {
                        schemaKey: generatedSchemaKey,
                        schema: parsedSchema,
                        tags: values.tags || [],
                        notes: values.notes || undefined,
                    },
                    {
                        onSuccess(newForm) {
                            // After creating, publish the form
                            publishMutate(
                                { schemaId: newForm.id },
                                {
                                    onSuccess() {
                                        message.success({
                                            content: 'Form created and published successfully',
                                            key: toastKey,
                                        });
                                        setIsPublishing(false);
                                        onClose();
                                    },
                                    onError(error) {
                                        setIsPublishing(false);
                                        message.destroy(toastKey);
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        },
                        onError(error) {
                            setIsPublishing(false);
                            message.destroy(toastKey);
                            errorMessage.show(error);
                        },
                    },
                );
            } catch {
                setIsPublishing(false);
                message.destroy(toastKey);
            }
        },
        [form, parsedSchema, generatedSchemaKey, createMutate, publishMutate],
    );

    return (
        <OverlayPage
            size="xLarge"
            title="Create form"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.formSchema.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) =>
                formGenerated ? (
                    <>
                        <Button type="secondary" onClick={onClose} disabled={isPublishing}>
                            Cancel
                        </Button>
                        <Button
                            type="secondary"
                            htmlType="submit"
                            form={formId}
                            disabled={!parsedSchema || isPublishing}
                        >
                            Save Draft
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => handleSaveAndPublish(onClose)}
                            disabled={!parsedSchema || isPublishing}
                            loading={isPublishing}
                        >
                            Publish
                        </Button>
                    </>
                ) : (
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                )
            }
        >
            {({ onClose }) => (
                <div>
                    {!formGenerated ? (
                        // Landing view: Show AI form generator
                        <SchemaAIHelper mode="landing" onSchemaGenerated={handleSchemaGenerated} />
                    ) : (
                        // Tabs view: Show form editor tabs
                        <Form
                            id={formId}
                            form={form}
                            layout="vertical"
                            onFinish={(values) => {
                                if (!parsedSchema) {
                                    message.error('Please fix form errors before saving');
                                    return;
                                }

                                const toastKey = 'form-create';
                                message.loading({ content: 'Creating...', key: toastKey });

                                createMutate(
                                    {
                                        schemaKey: generatedSchemaKey,
                                        schema: parsedSchema,
                                        tags: values.tags || [],
                                        notes: values.notes || undefined,
                                    },
                                    {
                                        onSuccess() {
                                            message.success({ content: 'Form created successfully', key: toastKey });
                                            onClose();
                                        },
                                        onError(error) {
                                            message.destroy(toastKey);
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            }}
                        >
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={[
                                    {
                                        key: 'preview',
                                        label: 'Form preview',
                                        children: (
                                            <SchemaPreviewTab
                                                parsedSchema={parsedSchema}
                                                formStore={formStore}
                                                onReset={handleReset}
                                                onSubmit={handleFormSubmit}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'general',
                                        label: 'Form settings',
                                        forceRender: true,
                                        children: (
                                            <SchemaGeneralTab
                                                mode="create"
                                                schemaKey={generatedSchemaKey}
                                                onTitleChange={handleTitleChange}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'editor',
                                        label: (
                                            <span className="flex items-center gap-2">
                                                <FileCode size={16} />
                                                Form editor
                                            </span>
                                        ),
                                        forceRender: true,
                                        children: (
                                            <SchemaEditorTab
                                                schemaJson={schemaJson}
                                                parseError={parseError}
                                                copied={copied}
                                                onSchemaChange={updateSchemaJson}
                                                onFormatJson={formatJson}
                                                onCopySchema={copySchema}
                                                onSchemaGenerated={handleSchemaGenerated}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Form>
                    )}
                </div>
            )}
        </OverlayPage>
    );
}
